import { secureLogger } from '../utils/secureLogger';

export interface NativeDeviceControlState {
  trusted_origin?: boolean;
  is_native_device_control_enabled?: boolean;
  frontend_url?: string;
  device_id?: string | null;
  ble_state?: string;
  connected_thinklet_device_id?: string | null;
  tracking?: boolean;
  pending_connect_target_device_id?: string | null;
  missing_permissions?: string[];
  permissions?: Record<string, boolean>;
}

export interface ThinkletScanResult {
  device_id?: string | null;
  name?: string;
  address?: string;
}

export interface NativeActionResult {
  requestId?: string | null;
  status?: 'accepted' | 'ok' | 'error' | string;
  code?: string;
  message?: string;
  data?: Record<string, unknown>;
}

export interface NativeDeviceError {
  requestId?: string | null;
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
}

interface AndroidDeviceBridge {
  isNativeDeviceControlEnabled?: () => boolean;
  getDeviceControlStateJson?: () => string;
  startBleScan?: (requestId: string) => void;
  connectThinklet?: (requestId: string, targetDeviceId: string) => void;
  disconnectThinklet?: (requestId: string) => void;
  startTracking?: (requestId: string) => void;
  stopTracking?: (requestId: string) => void;
  requestPermissions?: (requestId: string) => void;
  openAppSettings?: (requestId: string) => void;
}

declare global {
  interface Window {
    AndroidDevice?: AndroidDeviceBridge;
  }
}

export const EVENT_DEVICE_STATE_CHANGED = 'thinklet-native-device-state-changed';
export const EVENT_BLE_SCAN_RESULTS = 'thinklet-native-ble-scan-results';
export const EVENT_DEVICE_ERROR = 'thinklet-native-device-error';
export const EVENT_ACTION_RESULT = 'thinklet-native-device-action-result';

const safeBridgeCall = <T>(name: string, fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch (error) {
    secureLogger.error(`AndroidDevice bridge call failed: ${name}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    return fallback;
  }
};

const parseJson = <T>(value: string | undefined, fallback: T): T => {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

export const createNativeRequestId = (action: string): string => {
  const randomSuffix = Math.random().toString(16).slice(2, 8);
  return `${action}_${Date.now()}_${randomSuffix}`;
};

export const isNativeDeviceControlEnabled = (): boolean => {
  if (!window.AndroidDevice) {
    return false;
  }
  if (typeof window.AndroidDevice.isNativeDeviceControlEnabled === 'function') {
    return safeBridgeCall(
      'isNativeDeviceControlEnabled',
      () => window.AndroidDevice?.isNativeDeviceControlEnabled?.() === true,
      false
    );
  }
  return false;
};

export const getNativeDeviceControlState = (): NativeDeviceControlState => {
  if (!window.AndroidDevice?.getDeviceControlStateJson) {
    return {};
  }
  const raw = safeBridgeCall(
    'getDeviceControlStateJson',
    () => window.AndroidDevice?.getDeviceControlStateJson?.(),
    undefined
  );
  return parseJson<NativeDeviceControlState>(raw, {});
};

export const invokeStartBleScan = (requestId: string): void => {
  safeBridgeCall(
    'startBleScan',
    () => {
      window.AndroidDevice?.startBleScan?.(requestId);
      return undefined;
    },
    undefined
  );
};

export const invokeConnectThinklet = (requestId: string, targetDeviceId: string): void => {
  safeBridgeCall(
    'connectThinklet',
    () => {
      window.AndroidDevice?.connectThinklet?.(requestId, targetDeviceId);
      return undefined;
    },
    undefined
  );
};

export const invokeDisconnectThinklet = (requestId: string): void => {
  safeBridgeCall(
    'disconnectThinklet',
    () => {
      window.AndroidDevice?.disconnectThinklet?.(requestId);
      return undefined;
    },
    undefined
  );
};

export const invokeStartTracking = (requestId: string): void => {
  safeBridgeCall(
    'startTracking',
    () => {
      window.AndroidDevice?.startTracking?.(requestId);
      return undefined;
    },
    undefined
  );
};

export const invokeStopTracking = (requestId: string): void => {
  safeBridgeCall(
    'stopTracking',
    () => {
      window.AndroidDevice?.stopTracking?.(requestId);
      return undefined;
    },
    undefined
  );
};

export const invokeRequestPermissions = (requestId: string): void => {
  safeBridgeCall(
    'requestPermissions',
    () => {
      window.AndroidDevice?.requestPermissions?.(requestId);
      return undefined;
    },
    undefined
  );
};

export const invokeOpenAppSettings = (requestId: string): void => {
  safeBridgeCall(
    'openAppSettings',
    () => {
      window.AndroidDevice?.openAppSettings?.(requestId);
      return undefined;
    },
    undefined
  );
};

